import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FaqPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('Todas');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    document.title = 'Preguntas Frecuentes | FAQ Pintura Barcelona | PINTORDECO';
    
    // Update canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${siteUrl}/faq`);
    }
    
    // Update Open Graph meta tags
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', `${siteUrl}/faq`);
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Preguntas Frecuentes | FAQ Pintura Barcelona | PINTORDECO');
    }
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Preguntas frecuentes sobre pintura en Barcelona: precios, tiempos, proceso, materiales. Resuelve tus dudas con PINTORDECO. ☎️ 690 22 09 44');
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Preguntas frecuentes sobre pintura en Barcelona: precios, tiempos, proceso, materiales. Resuelve tus dudas con PINTORDECO.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'precio pintar piso Barcelona, cuanto cuesta pintar, preguntas pintura, FAQ pintor Barcelona');
    }

    // FAQ Structured Data
    const scriptLD = document.createElement('script');
    scriptLD.type = 'application/ld+json';
    scriptLD.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${siteUrl}/faq#faqpage`,
      "url": `${siteUrl}/faq`,
      "name": "Preguntas Frecuentes | FAQ Pintura Barcelona",
      "inLanguage": "es-ES",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": "PINTORDECO Barcelona"
      },
      "mainEntity": [
        {
          "@type": "Question",
          "name": "¿Cuánto cuesta pintar un piso en Barcelona?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "El precio de pintar un piso en Barcelona depende de varios factores: metros cuadrados, estado de las paredes, si necesita reparaciones previas, calidad de la pintura y acabados deseados. Como referencia, un piso de 60-80m² puede costar entre 1.200€ y 2.500€. Te recomendamos solicitar un presupuesto personalizado gratuito para conocer el precio exacto de tu proyecto."
          }
        },
        {
          "@type": "Question",
          "name": "¿Cómo se calcula el presupuesto de pintura?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Calculamos el presupuesto considerando: superficie a pintar (m²), estado actual de paredes y techos, trabajos previos necesarios (reparaciones, alisado de gotelé), tipo y calidad de pintura, número de manos necesarias, protección y limpieza, y complejidad del trabajo. Cada presupuesto es personalizado según las características específicas de tu proyecto."
          }
        },
        {
          "@type": "Question",
          "name": "¿Cuánto tiempo tarda en pintarse un piso?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Un piso de 60-80m² generalmente requiere entre 3 y 5 días laborales, incluyendo preparación, pintura y secado. El tiempo puede variar según el estado inicial, trabajos adicionales necesarios y número de capas. Si se requiere alisado de gotelé, el tiempo se extiende 2-3 días más. Te damos un plazo realista en el presupuesto."
          }
        },
        {
          "@type": "Question",
          "name": "¿Debo vaciar completamente el piso antes de pintar?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No es necesario vaciar completamente el piso. Protegemos cuidadosamente todos los muebles y suelos. Si es posible, recomendamos retirar objetos pequeños y decoración de las paredes. Los muebles grandes los movemos nosotros al centro de las habitaciones y los cubrimos con plásticos protectores."
          }
        },
        {
          "@type": "Question",
          "name": "¿Qué tipo de pintura recomiendan?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Recomendamos pintura según el uso de cada espacio: para salones y dormitorios, pintura plástica mate lavable; en cocinas y baños, pintura satinada resistente a la humedad; para techos, pintura mate específica; en zonas de alto tránsito, pintura satinada más resistente. Siempre usamos marcas de calidad profesional para garantizar durabilidad y acabado perfecto."
          }
        },
        {
          "@type": "Question",
          "name": "¿Cuánto tarda en secar la pintura?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "El tiempo de secado varía según el tipo de pintura y condiciones ambientales. Al tacto seca en 1-2 horas, pero recomendamos esperar 24 horas antes de volver a usar la habitación normalmente. Para un secado completo y curado total, esperar 7-10 días antes de limpiar las paredes o colgar cuadros."
          }
        },
        {
          "@type": "Question",
          "name": "¿Trabajan en fines de semana o fuera de horario?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, podemos adaptarnos a tus necesidades. Para locales comerciales frecuentemente trabajamos en horarios nocturnos o fines de semana para no interferir con la actividad. En el caso de viviendas, trabajamos principalmente en horario laboral respetando las normas de la comunidad, pero podemos coordinar otros horarios según disponibilidad."
          }
        },
        {
          "@type": "Question",
          "name": "¿Qué fotos necesitan para hacer un presupuesto?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Para hacer un presupuesto preciso, necesitamos fotos de: todas las paredes de cada habitación, techos (especialmente si tienen gotelé), estado de las superficies (grietas, manchas, desperfectos), puertas y ventanas, y una foto general de cada espacio. También ayuda un vídeo recorriendo el piso. Cuanta más información visual tengamos, más exacto será el presupuesto."
          }
        }
      ]
    });
    document.head.appendChild(scriptLD);
    
    return () => {
      if (scriptLD.parentNode) {
        scriptLD.parentNode.removeChild(scriptLD);
      }
    };
  }, []);

  const categorias = ['Todas', 'Precios', 'Proceso', 'Materiales', 'Tiempos'];

  const faqs = [
    {
      categoria: 'Precios',
      pregunta: '¿Cuánto cuesta pintar un piso en Barcelona?',
      respuesta: 'El precio de pintar un piso en Barcelona depende de varios factores: metros cuadrados, estado de las paredes, si necesita reparaciones previas, calidad de la pintura y acabados deseados. Como referencia orientativa, un piso de 60-80m² puede costar entre 1.200€ y 2.500€. Sin embargo, cada proyecto es único. Te recomendamos <a href="/contacto" class="text-gray-700 hover:text-[#14B8A6] transition-colors underline">solicitar un presupuesto personalizado gratuito</a> para conocer el precio exacto de tu proyecto. Valoramos tu caso específicamente y te enviamos un presupuesto detallado sin compromiso.',
    },
    {
      categoria: 'Precios',
      pregunta: '¿Cómo se calcula el presupuesto de pintura?',
      respuesta: 'Calculamos el presupuesto considerando múltiples factores: <strong>Superficie a pintar</strong> (metros cuadrados de paredes y techos), <strong>estado actual</strong> de las superficies (si necesitan reparaciones), <strong>trabajos previos</strong> necesarios (reparación de grietas, alisado de gotelé, lijado), <strong>tipo y calidad de pintura</strong> según tus preferencias, <strong>número de manos</strong> necesarias para un acabado perfecto, <strong>protección y limpieza</strong>, y <strong>complejidad del trabajo</strong> (altura, accesos, muebles). Cada presupuesto es personalizado y detallado según las características específicas de tu proyecto. Siempre por escrito y sin letra pequeña. <a href="/empresa" class="text-gray-700 hover:text-[#14B8A6] transition-colors underline">Conoce cómo trabajamos</a>.',
    },
    {
      categoria: 'Tiempos',
      pregunta: '¿Cuánto tiempo tarda en pintarse un piso?',
      respuesta: 'El tiempo necesario varía según el tamaño y estado del piso. Como referencia: un <strong>piso de 60-80m²</strong> generalmente requiere entre <strong>3 y 5 días laborales</strong>, incluyendo preparación, protección, pintura de paredes y techos, y secado entre capas. Si el piso necesita <strong>reparaciones previas o alisado de gotelé</strong>, el tiempo se extiende 2-3 días adicionales. Para <strong>locales comerciales</strong>, podemos trabajar con mayor intensidad horaria para reducir el plazo. Siempre te damos un plazo realista en el presupuesto y lo cumplimos. No prometemos plazos irreales que luego no podamos cumplir. <a href="/servicios" class="text-gray-700 hover:text-[#14B8A6] transition-colors underline">Ver detalles de cada servicio</a>.',
    },
    {
      categoria: 'Proceso',
      pregunta: '¿Debo vaciar completamente el piso antes de pintar?',
      respuesta: '<strong>No es necesario vaciar completamente el piso.</strong> Nosotros nos encargamos de proteger cuidadosamente todos los muebles, suelos y elementos que no se pintan. Lo que sí recomendamos es: retirar objetos pequeños, decoración de las paredes y plantas; vaciar armarios empotrados si se van a pintar por dentro; y dejar acceso libre a las habitaciones. Los muebles grandes (sofás, camas, armarios) los movemos nosotros al centro de las habitaciones y los cubrimos con plásticos protectores de alta resistencia. Al finalizar, lo dejamos todo en su sitio. <a href="/empresa" class="text-gray-700 hover:text-[#14B8A6] transition-colors underline">Descubre nuestro proceso de trabajo</a>.',
    },
    {
      categoria: 'Proceso',
      pregunta: '¿Cómo protegen los muebles y suelos?',
      respuesta: 'La protección es fundamental en nuestro trabajo. Utilizamos <strong>plásticos gruesos</strong> para cubrir completamente los muebles, <strong>cartones y lonas</strong> para proteger suelos (incluso si son de parquet o mármol), <strong>cinta de carrocero de calidad</strong> que no deja residuos para ventanas, marcos y rodapiés, y <strong>protección específica</strong> para zonas comunes (escalera, ascensor). Si trabajamos en un edificio con comunidad de vecinos, protegemos también el portal y las zonas de paso. Al finalizar, retiramos todas las protecciones y dejamos el espacio perfectamente limpio. <a href="/" class="text-gray-700 hover:text-[#14B8A6] transition-colors underline">Conoce más sobre nuestros pintores profesionales</a>.',
    },
    {
      categoria: 'Materiales',
      pregunta: '¿Qué tipo de pintura recomiendan?',
      respuesta: 'Recomendamos la pintura según el uso específico de cada espacio: <strong>Salones y dormitorios:</strong> pintura plástica mate lavable de alta calidad, que no refleja luz y disimula pequeñas imperfecciones. <strong>Cocinas y baños:</strong> pintura satinada o semi-mate, más resistente a la humedad y fácil de limpiar. <strong>Techos:</strong> pintura mate específica para techos, con mayor rendimiento y menos salpicaduras. <strong>Zonas de alto tránsito:</strong> pintura satinada más resistente al roce. <strong>Exterior/fachadas:</strong> pintura específica para exteriores resistente a la intemperie. Trabajamos con marcas profesionales de calidad (Bruguer, Jotun, Tollens) para garantizar durabilidad y acabado perfecto. <a href="/servicios" class="text-gray-700 hover:text-[#14B8A6] transition-colors underline">Ver servicios de pintura disponibles</a>.',
    },
    {
      categoria: 'Materiales',
      pregunta: '¿La pintura tiene olores fuertes? ¿Es segura?',
      respuesta: 'Utilizamos <strong>pinturas de baja emisión de COV</strong> (compuestos orgánicos volátiles), mucho más seguras y con menos olor que las pinturas tradicionales. Aun así, durante la aplicación y secado habrá cierto olor característico, por lo que recomendamos <strong>ventilar bien</strong> las habitaciones. Las pinturas actuales son seguras y cumplen todas las normativas europeas. Si hay personas sensibles, niños pequeños o mujeres embarazadas, pueden permanecer en otra zona de la casa o volver cuando la pintura esté seca (24-48 horas). También ofrecemos pinturas ecológicas especiales bajo petición. <a href="/contacto" class="text-gray-700 hover:text-[#14B8A6] transition-colors underline">Consulta opciones de pintura</a>.',
    },
    {
      categoria: 'Tiempos',
      pregunta: '¿Cuánto tarda en secar la pintura?',
      respuesta: 'El tiempo de secado varía según el tipo de pintura, temperatura y humedad ambiental: <strong>Secado al tacto:</strong> 1-2 horas (puedes tocar la pared sin mancharte). <strong>Secado entre capas:</strong> 4-6 horas (para aplicar la siguiente mano). <strong>Secado para uso normal:</strong> 24 horas (puedes volver a usar la habitación y colocar muebles pegados a la pared). <strong>Curado completo:</strong> 7-10 días (es cuando alcanza su máxima resistencia). Recomendamos esperar al menos 7 días antes de limpiar las paredes, colgar cuadros o pegar muebles a las paredes. Durante este tiempo, ventilar regularmente ayuda a acelerar el proceso.',
    },
    {
      categoria: 'Proceso',
      pregunta: '¿Trabajan en fines de semana o fuera de horario?',
      respuesta: 'Sí, podemos adaptarnos a tus necesidades. Nuestro <strong>horario habitual</strong> es de lunes a viernes de 8:00 a 20:00 y sábados de 9:00 a 14:00, respetando las normativas de las comunidades de vecinos. Para <strong>locales comerciales</strong>, frecuentemente trabajamos en horarios nocturnos o fines de semana completos para no interferir con la actividad del negocio. En viviendas, si la comunidad lo permite y acordamos disponibilidad, también podemos trabajar en otros horarios. Los trabajos fuera de horario laboral pueden tener un pequeño suplemento. <a href="/contacto" class="text-gray-700 hover:text-[#14B8A6] transition-colors underline">Consúltanos tu caso específico</a>.',
    },
    {
      categoria: 'Proceso',
      pregunta: '¿Qué fotos necesitan para hacer un presupuesto?',
      respuesta: 'Para hacer un presupuesto preciso y ajustado, necesitamos fotos o vídeo de: <strong>Todas las paredes</strong> de cada habitación (al menos una foto por pared). <strong>Todos los techos</strong>, especialmente si tienen gotelé o textura. <strong>Estado de las superficies:</strong> grietas, manchas, humedad, desperfectos visibles. <strong>Puertas, ventanas y rodapiés</strong> si también se van a pintar. <strong>Foto general</strong> de cada espacio para ver la distribución y mobiliario. Un <strong>vídeo recorriendo todo el piso</strong> también es muy útil. Cuanta más información visual tengamos, más exacto y ajustado será el presupuesto. Puedes enviárnoslas por WhatsApp fácilmente. <a href="/contacto" class="text-gray-700 hover:text-[#14B8A6] transition-colors underline">Solicitar presupuesto ahora</a>.',
    },
  ];

  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory = activeCategory === 'Todas' || faq.categoria === activeCategory;
    const matchesSearch = faq.pregunta.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          faq.respuesta.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <nav className="text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-accent cursor-pointer">Inicio</Link>
            <span className="mx-2">/</span>
            <span className="text-primary font-semibold">Preguntas frecuentes</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight">
              Preguntas frecuentes
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Resolvemos tus dudas sobre nuestros <Link to="/servicios" className="text-gray-600 hover:text-accent transition-colors underline">servicios de pintura en Barcelona</Link>. Si no encuentras la respuesta, <Link to="/contacto" className="text-gray-600 hover:text-accent transition-colors underline">contáctanos directamente</Link>.
            </p>

            {/* Buscador */}
            <div className="max-w-2xl mx-auto relative">
              <input
                type="text"
                placeholder="Busca tu pregunta..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-6 py-4 pl-14 rounded-full border-2 border-gray-300 focus:border-accent focus:outline-none text-lg"
              />
              <i className="ri-search-line absolute left-5 top-1/2 transform -translate-y-1/2 text-2xl text-gray-400"></i>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categorías */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-2 overflow-x-auto">
            {categorias.map((categoria) => (
              <button
                key={categoria}
                onClick={() => setActiveCategory(categoria)}
                className={`whitespace-nowrap px-6 py-2 rounded-full font-medium transition-all cursor-pointer ${
                  activeCategory === categoria
                    ? 'bg-accent text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {categoria}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Acordeón de Preguntas */}
      <section className="py-20 bg-light">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-question-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-xl text-gray-500">No se encontraron preguntas con ese criterio</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 pr-4">
                      <span className="text-xs font-semibold text-accent uppercase tracking-wide mb-1 block">
                        {faq.categoria}
                      </span>
                      <h3 className="text-lg font-bold text-primary">{faq.pregunta}</h3>
                    </div>
                    <i
                      className={`text-2xl text-accent transition-transform ${
                        openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>

                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 py-5 bg-gray-50 border-t border-gray-200">
                          <p
                            className="text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: faq.respuesta }}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Inferior */}
      <section className="py-16 bg-gradient-to-r from-accent/10 to-primary/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-primary mb-4">
              ¿Tienes otra pregunta?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Estamos aquí para ayudarte. <Link to="/contacto" className="text-gray-600 hover:text-accent transition-colors font-semibold underline">Contacta con nosotros</Link> por WhatsApp y te responderemos enseguida. También puedes conocer más sobre <Link to="/empresa" className="text-gray-600 hover:text-accent transition-colors underline">nuestro equipo profesional</Link>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/34690220944"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap inline-flex items-center justify-center space-x-3 px-8 py-4 bg-accent text-white rounded-full font-semibold text-lg hover:bg-accent/90 transition-all cursor-pointer shadow-lg"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                <span>Contactar por WhatsApp</span>
              </a>
              <Link
                to="/contacto"
                className="whitespace-nowrap inline-flex items-center justify-center space-x-3 px-8 py-4 bg-primary text-white rounded-full font-semibold text-lg hover:bg-primary/90 transition-all cursor-pointer shadow-lg"
              >
                <i className="ri-mail-line text-xl"></i>
                <span>Enviar formulario</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FaqPage;
